import { Trans, useLingui } from '@lingui/react/macro';
import { useFlag } from '@repo/flags/react';
import { Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { BaseModal } from '@/components/ui/base-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { OptimizeDescriptionModal } from './optimize-description-modal';
import { OptimizeNameModal } from './optimize-name-modal';
import {
  optimizeDescriptionStep1,
  optimizeTemplateNameStep1,
} from '@/domain/templates/ai-optimize';
import { filterByIds, toSelectOptions } from '@/lib/utils';
import { TEMPLATE_DESCRIPTION_MAX_LENGTH, type Site, type TemplateBasics } from '@/types/template';

import { ScalingBaseline } from './baseline-editor';

/**
 * Modal for capturing a template's basics — name, optional description, scope
 * (organisation-wide vs specific sites) and the site list. Used in the v2 Build
 * workspace both as the create-flow gate (mode `create`, before the workspace
 * is usable) and as the header "Edit details" editor (mode `edit`).
 *
 * It is controlled and pure: the host owns the live site list and the persisted
 * basics. A local working copy is committed only via {@link onSubmit} (the
 * primary footer button); every dismissal route (Cancel, the close control,
 * Escape, outside-click) goes through {@link onDismiss} instead, so the host can
 * treat an abandoned create differently from an abandoned edit. The AI
 * "Optimise name" affordance reuses {@link OptimizeNameModal}; while it is open
 * the basics dialog is hidden to avoid nesting two dialogs.
 */
export function BasicsModal({
  mode,
  open,
  initialBasics,
  sites,
  onSubmit,
  onDismiss,
}: BasicsModalProps) {
  const { t } = useLingui();
  const [isScalingEnabled] = useFlag('TEMPLATE_BUILDER_SCALING');
  const [working, setWorking] = useState<TemplateBasics>(initialBasics);
  // The organisation-vs-specific radio is UI-only state; the contract scope just
  // carries `sites` (empty ⇒ all). Tracked separately so "specific" can be
  // selected before any site is picked.
  const [scopeKind, setScopeKind] = useState<'organisation' | 'specific'>(
    (initialBasics.scope.sites?.length ?? 0) > 0 ? 'specific' : 'organisation',
  );
  const [optimiseOpen, setOptimiseOpen] = useState(false);
  const [optimiseDescOpen, setOptimiseDescOpen] = useState(false);

  // Re-seed the working copy whenever the modal (re)opens, so a previously
  // abandoned edit doesn't leak into the next open.
  useEffect(() => {
    if (open) {
      setWorking(initialBasics);
      setScopeKind((initialBasics.scope.sites?.length ?? 0) > 0 ? 'specific' : 'organisation');
    }
  }, [open, initialBasics]);

  const siteOptions = useMemo(() => toSelectOptions(sites), [sites]);
  const selectedSiteIds = (working.scope.sites ?? []).map((site) => site.id);

  const applicableSites = useMemo(() => {
    if (scopeKind === 'organisation') return t`Organisation-wide`;
    const selectedNames = (working.scope.sites ?? []).map((site) => site.name);
    return selectedNames.length > 0 ? selectedNames.join(', ') : t`No sites selected`;
  }, [scopeKind, working.scope.sites, t]);

  /** Builds the persisted basics: scope carries sites only when "specific". */
  function buildBasics(): TemplateBasics {
    return { ...working, scope: scopeKind === 'specific' ? working.scope : {} };
  }

  const hasName = working.name.trim().length > 0;
  const descriptionLength = (working.description ?? '').length;
  const descriptionTooLong = descriptionLength > TEMPLATE_DESCRIPTION_MAX_LENGTH;
  const submitLabel = mode === 'create' ? t`Continue` : t`Save changes`;

  return (
    <>
      <BaseModal
        open={open && !optimiseOpen && !optimiseDescOpen}
        onOpenChange={(next) => {
          if (!next) onDismiss();
        }}
        title={mode === 'create' ? <Trans>Create template</Trans> : <Trans>Template basics</Trans>}
        description={<Trans>Name your template and choose where it applies.</Trans>}
        className="rotaready:max-w-2xl"
        footer={
          <div className="flex w-full flex-col-reverse items-center justify-between gap-2 sm:flex-row">
            <Button variant="outline" onClick={onDismiss}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              onClick={() => onSubmit(buildBasics())}
              disabled={!hasName || descriptionTooLong}
            >
              {submitLabel}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 rotaready:space-y-0">
          <div
            className={
              'space-y-2 rotaready:mb-5 rotaready:grid ' +
              'rotaready:grid-cols-[200px_1fr] rotaready:grid-rows-[auto_1fr] ' +
              'rotaready:gap-x-5 rotaready:gap-y-2 rotaready:space-y-0'
            }
          >
            <Label
              htmlFor="basics-name"
              className={
                'gap-0 rotaready:col-start-1 rotaready:row-start-2 ' +
                'rotaready:justify-end rotaready:self-center ' +
                'rotaready:whitespace-nowrap rotaready:text-base rotaready:font-semibold ' +
                "rotaready:after:content-['*'] rotaready:after:text-destructive"
              }
            >
              <Trans>Template name</Trans>
            </Label>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t`AI suggest name`}
              onClick={() => setOptimiseOpen(true)}
              disabled={!hasName}
              className="rotaready:col-start-2 rotaready:justify-self-end"
            >
              <Sparkles className="mr-1 h-3 w-3" />
              <Trans>AI suggest</Trans>
            </Button>
            <Input
              id="basics-name"
              value={working.name}
              onChange={(e) => setWorking({ ...working, name: e.target.value })}
              placeholder={t`e.g. Weekend brunch coverage`}
              aria-required="true"
              className="rotaready:col-start-2 rotaready:row-start-2 rotaready:h-10 rotaready:text-base"
            />
          </div>

          <div
            className={
              'space-y-2 rotaready:mb-5 rotaready:grid ' +
              'rotaready:grid-cols-[200px_1fr] rotaready:grid-rows-[auto_1fr_auto] ' +
              'rotaready:gap-x-5 rotaready:gap-y-2 rotaready:space-y-0'
            }
          >
            <Label
              htmlFor="basics-description"
              className={
                'rotaready:col-start-1 rotaready:row-start-2 ' +
                'rotaready:justify-end rotaready:self-center ' +
                'rotaready:whitespace-nowrap rotaready:text-base rotaready:font-semibold'
              }
            >
              <Trans>Description</Trans>
            </Label>
            <Button
              variant="ghost"
              size="sm"
              aria-label={t`AI suggest description`}
              onClick={() => setOptimiseDescOpen(true)}
              disabled={!hasName}
              className="rotaready:col-start-2 rotaready:justify-self-end"
            >
              <Sparkles className="mr-1 h-3 w-3" />
              <Trans>AI suggest</Trans>
            </Button>
            <Textarea
              id="basics-description"
              value={working.description ?? ''}
              onChange={(e) => setWorking({ ...working, description: e.target.value })}
              rows={3}
              aria-invalid={descriptionTooLong}
              placeholder={t`Briefly describe when this template should be used.`}
              className="rotaready:col-start-2 rotaready:row-start-2 rotaready:text-base"
            />
            <div className="flex items-start justify-between gap-2 rotaready:col-start-2 rotaready:row-start-3">
              {descriptionTooLong ? (
                <p role="alert" className="text-destructive text-sm">
                  <Trans>
                    Description must be {TEMPLATE_DESCRIPTION_MAX_LENGTH} characters or fewer.
                  </Trans>
                </p>
              ) : (
                <span />
              )}
              <p
                className={`text-right text-sm ${descriptionTooLong ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                {descriptionLength}/{TEMPLATE_DESCRIPTION_MAX_LENGTH}
              </p>
            </div>
          </div>

          <div
            className={
              'space-y-2 rotaready:mb-5 rotaready:grid ' +
              'rotaready:grid-cols-[200px_1fr] rotaready:gap-x-5 ' +
              'rotaready:space-y-0'
            }
          >
            <Label
              className={
                'rotaready:justify-end rotaready:self-center ' +
                'rotaready:whitespace-nowrap rotaready:text-base rotaready:font-semibold'
              }
            >
              <Trans>Scope</Trans>
            </Label>
            <div className="space-y-2">
              <RadioGroup
                value={scopeKind}
                onValueChange={(value) => setScopeKind(value as 'organisation' | 'specific')}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="basics-scope-organisation" value="organisation" />
                  <Label htmlFor="basics-scope-organisation" className="rotaready:text-base">
                    <Trans>Organisation-wide</Trans>
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="basics-scope-specific" value="specific" />
                  <Label htmlFor="basics-scope-specific" className="rotaready:text-base">
                    <Trans>Specific sites</Trans>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {scopeKind === 'specific' && (
            <div
              className={
                'space-y-2 rotaready:mb-5 rotaready:grid ' +
                'rotaready:grid-cols-[200px_1fr] rotaready:gap-x-5 ' +
                'rotaready:space-y-0'
              }
            >
              <Label
                htmlFor="basics-sites"
                className={
                  'rotaready:justify-end rotaready:self-center ' +
                  'rotaready:whitespace-nowrap rotaready:text-base rotaready:font-semibold'
                }
              >
                <Trans>Sites</Trans>
              </Label>
              <div>
                <MultiSelect
                  options={siteOptions}
                  value={selectedSiteIds}
                  onValueChange={(siteIds) =>
                    setWorking({
                      ...working,
                      scope: {
                        sites: filterByIds(sites, siteIds).map((site) => ({
                          id: site.id,
                          name: site.name,
                        })),
                      },
                    })
                  }
                  placeholder={t`Select sites…`}
                  triggerClassName="rotaready:text-base"
                />
              </div>
            </div>
          )}

          {isScalingEnabled && (
            <ScalingBaseline
              value={working.demandScaling}
              onChange={(demandScaling) => setWorking({ ...working, demandScaling })}
            />
          )}
        </div>
      </BaseModal>

      {optimiseOpen && (
        <OptimizeNameModal
          currentName={working.name}
          generate={() =>
            optimizeTemplateNameStep1({ templateName: working.name, applicableSites })
          }
          onAccept={(name) => {
            setWorking((w) => ({ ...w, name }));
            setOptimiseOpen(false);
          }}
          onCancel={() => setOptimiseOpen(false)}
        />
      )}

      {optimiseDescOpen && (
        <OptimizeDescriptionModal
          currentName={working.name}
          currentDescription={working.description ?? ''}
          generate={() =>
            optimizeDescriptionStep1({
              templateName: working.name,
              applicableSites,
              currentDescription: working.description,
            })
          }
          onAccept={(description) => {
            setWorking((w) => ({ ...w, description }));
            setOptimiseDescOpen(false);
          }}
          onCancel={() => setOptimiseDescOpen(false)}
        />
      )}
    </>
  );
}

interface BasicsModalProps {
  /** `create` gates the new-template flow; `edit` re-opens basics from the header. */
  mode: 'create' | 'edit';
  /** Whether the modal is shown. */
  open: boolean;
  /** Seed for the working copy; re-applied whenever the modal opens. */
  initialBasics: TemplateBasics;
  /** Live site list for the specific-sites picker. */
  sites: Site[];
  /** Commits the edited basics (the only submit path). */
  onSubmit: (basics: TemplateBasics) => void;
  /** Fired for every dismissal (Cancel, close control, Escape, outside-click). */
  onDismiss: () => void;
}
